import React, { useEffect, useRef } from "react";
import L from "leaflet";
import {
  MapContainer as LeafletMapContainer,
  TileLayer,
  FeatureGroup,
} from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import {
  Box,
  IconButton,
  FormControlLabel,
  Checkbox,
  Button,
  Popover,
} from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import PaletteIcon from "@mui/icons-material/Palette";
import { SketchPicker } from "react-color";
import MapController from "./MapController";

const MapContainer = ({
  currentLocation,
  zoomLevel,
  isFullScreen,
  toggleFullScreen,
  drawingColor,
  handleCreate,
  handleDelete,
  featureGroupRef,
  mapRef,
  bounds,
  currentFolder,
  polygonList,
  getShapesIdsForFolder,
  geoLocationJSONData,
  showUserLocations,
  setShowUserLocations,
  activeDrawingTool,
  handleColorPickerOpen,
  handleColorPickerClose,
  colorPickerAnchor,
  handleColorChange,
  optionsCheckBox,
  setOptionsCheckBox,
}) => {
  // Modify L.drawLocal to change toolbar tooltip
  useEffect(() => {
    L.drawLocal.draw.toolbar.buttons.polyline = "Create Route";
    L.drawLocal.draw.toolbar.buttons.polygon = "Create Zone";
  }, []);

  // Ensure mapRef is initialized
  useEffect(() => {
    if (mapRef.current) {
      console.log("Map is ready", mapRef.current);
      // Perform any additional setup or actions here
    }
  }, [mapRef]);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        position: isFullScreen ? "fixed" : "relative",
        top: 0,
        left: 0,
        zIndex: isFullScreen ? 1000000 : 1,
      }}
    >
      {/* Full-Screen Toggle Icon */}
      <IconButton
        onClick={toggleFullScreen}
        sx={{
          position: "absolute",
          top: 10,
          left: 55,
          zIndex: 1100,
          backgroundColor: "rgba(255, 255, 255)",
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255,0.8)",
          },
        }}
      >
        {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
      </IconButton>

      {/* Controls Box */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          position: "absolute",
          top: 10,
          right: 110,
          zIndex: 1000,
        }}
      >
        {currentFolder?.type === "user" ? (
          <>
            <FormControlLabel
              checked={optionsCheckBox?.showZones}
              onChange={() =>
                setOptionsCheckBox((prev) => ({
                  ...prev,
                  showZones: !prev.showZones,
                }))
              }
              control={<Checkbox />}
              label="Zones"
              sx={{
                zIndex: 1000,
                backgroundColor: "#4a4e69",
                borderRadius: 2,
                pr: 1,
              }}
            />
            <FormControlLabel
              checked={optionsCheckBox?.showRoutes}
              onChange={() =>
                setOptionsCheckBox((prev) => ({
                  ...prev,
                  showRoutes: !prev.showRoutes,
                }))
              }
              control={<Checkbox />}
              label="Routes"
              sx={{
                zIndex: 1000,
                backgroundColor: "#4a4e69",
                borderRadius: 2,
                pr: 1,
              }}
            />
          </>
        ) : (
          <>
            <FormControlLabel
              checked={optionsCheckBox?.showOverlapping}
              onChange={() =>
                setOptionsCheckBox((prev) => ({
                  ...prev,
                  showOverlapping: !prev.showOverlapping,
                }))
              }
              control={<Checkbox />}
              label="Overlapping"
              sx={{
                zIndex: 1000,
                backgroundColor: "#4a4e69",
                borderRadius: 2,
                pr: 1,
              }}
            />
          </>
        )}
        <FormControlLabel
          checked={optionsCheckBox?.showUsers}
          onChange={() =>
            setOptionsCheckBox((prev) => ({
              ...prev,
              showUsers: !prev.showUsers,
            }))
          }
          control={<Checkbox />}
          label="Users"
          sx={{
            zIndex: 1000,
            backgroundColor: "#4a4e69",
            borderRadius: 2,
            pr: 1,
          }}
        />
      </Box>

      {/* Color Picker Button */}
      {activeDrawingTool && (
        <Button
          startIcon={<PaletteIcon />}
          onClick={handleColorPickerOpen}
          variant="contained"
          sx={{
            position: "absolute",
            top: 10,
            right: 50,
            zIndex: 1000,
            padding: "0px",
            backgroundColor: drawingColor,
            "&:hover": {
              backgroundColor: drawingColor,
              filter: "brightness(0.9)",
            },
          }}
        />
      )}

      {/* Color Picker Popover */}
      <Popover
        open={Boolean(colorPickerAnchor)}
        anchorEl={colorPickerAnchor}
        onClose={handleColorPickerClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <SketchPicker
          color={drawingColor}
          onChange={handleColorChange}
          disableAlpha
          onChangeComplete={handleColorChange}
        />
      </Popover>

      <LeafletMapContainer
        key={currentLocation.toString()}
        center={currentLocation}
        zoom={zoomLevel}
        style={{ height: "100%", width: "100%", zIndex: 1 }}
        ref={mapRef}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        <FeatureGroup ref={featureGroupRef}>
          <EditControl
            key={drawingColor}
            position="topright"
            onCreated={handleCreate}
            onDeleted={handleDelete}
            draw={{
              polyline: {
                shapeOptions: { color: drawingColor },
                showLength: true,
              },
              polygon: {
                shapeOptions: { color: drawingColor },
                showArea: false,
                showLength: true,
              },
              rectangle: false,
              circle: false,
              marker: false,
              circlemarker: false,
              markerOptions: {
                icon: L.icon({
                  iconUrl: "/path/to/marker-icon.png",
                  iconSize: [32, 48],
                }),
              },
            }}
          />
        </FeatureGroup>

        <MapController
          bounds={bounds}
          polygonData={
            currentFolder?.type === "project" ||
            currentFolder?.type === "zones" ||
            currentFolder?.type === "routes"
              ? geoLocationJSONData
              : currentFolder?.type === "user" &&
                currentFolder?.userData?.latitude &&
                currentFolder?.userData?.longitude
              ? [
                  {
                    latitude: currentFolder?.userData?.latitude,
                    longitude: currentFolder?.userData?.longitude,
                  },
                ]
              : polygonList.filter((item) =>
                  getShapesIdsForFolder(currentFolder).includes(item.shapeId)
                )
          }
          type={
            currentFolder?.type === "project" ||
            currentFolder?.type === "zones" ||
            currentFolder?.type === "routes"
              ? "geojson"
              : currentFolder?.type === "user"
              ? "user"
              : "polygonData"
          }
        />
      </LeafletMapContainer>
    </Box>
  );
};

export default MapContainer;
